import { useMemo } from 'react';

import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { MutationError } from '../../shared/ui/mutation-error';
import { SectionPanel } from '../../shared/ui/section-panel';
import { Segmented } from '../../shared/ui/segmented';
import { Select } from '../../shared/ui/select';
import { browserZone, zoneLabel, zoneOptions } from '../../entities/user';
import type { TAccountPreferences } from './use-account-preferences';
import type { TTheme } from './theme';

const THEMES: readonly { value: TTheme; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
];

// Stands for "no zone picked" (the API's null). Not '': Radix Select treats an empty value as
// "nothing selected" and drew a blank field for everyone who never picked a zone.
const FOLLOW = '@browser';

export const AccountDisplay = ({ account }: { account: TAccountPreferences }) => {
  const { draft, change } = account;
  const here = browserZone();
  const options = useMemo(
    () => [
      { value: FOLLOW, label: `This browser's zone · ${zoneLabel(here)}` },
      ...zoneOptions(draft.timezone ?? here),
    ],
    [draft.timezone, here],
  );

  return (
    <SectionPanel title="Display" note="how hookpipe looks for you, in every organization">
      <div className="grid grid-cols-2 gap-5 px-card py-4">
        <div>
          <FieldLabel>Theme</FieldLabel>
          <Segmented
            fill
            aria-label="Theme"
            segments={THEMES}
            value={draft.theme}
            onChange={(theme) => change({ theme })}
          />
          <Hint>System follows your computer’s light or dark setting.</Hint>
        </div>
        <div>
          <FieldLabel htmlFor="account-zone">Time zone</FieldLabel>
          <Select
            id="account-zone"
            value={draft.timezone ?? FOLLOW}
            onChange={(timezone) => change({ timezone: timezone === FOLLOW ? null : timezone })}
            options={options}
          />
          <Hint>
            {draft.timezone === null
              ? `Times are shown in ${zoneLabel(here)}, where this browser is.`
              : `Times are shown in ${zoneLabel(draft.timezone)} on every browser you use.`}{' '}
            Type while the list is open to jump to a zone.
          </Hint>
        </div>
      </div>
      {account.save.isError && (
        <div className="px-card pb-4">
          <MutationError error={account.save.error} />
        </div>
      )}
    </SectionPanel>
  );
};
